import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Divider,
  LinearProgress,
  Badge,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import WorkIcon from '@mui/icons-material/Work';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { apiService } from '../api';

function Team() {
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    role: 'Associate',
    specialty: '',
    email: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [membersResponse, activityResponse] = await Promise.all([
        apiService.getTeamMembers(),
        apiService.getTeamActivity()
      ]);
      
      setTeamMembers(membersResponse.data.members || []);
      setActivityFeed(activityResponse.data.activities || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching team data:', err);
      setError('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (member = null) => {
    if (member) {
      setEditingMember(member);
      setFormData({
        name: member.name,
        role: member.role,
        specialty: member.specialty || '',
        email: member.email
      });
    } else {
      setEditingMember(null);
      setFormData({
        name: '',
        role: 'Associate',
        specialty: '',
        email: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMember(null);
    setFormData({
      name: '',
      role: 'Associate',
      specialty: '',
      email: ''
    });
  };

  const handleSaveMember = async () => {
    try {
      if (editingMember) {
        await apiService.updateTeamMember(editingMember.id, formData);
      } else {
        await apiService.createTeamMember(formData);
      }
      
      handleCloseDialog();
      fetchData();
    } catch (err) {
      console.error('Error saving member:', err);
      setError('Failed to save team member');
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;
    
    try {
      await apiService.deleteTeamMember(memberId);
      fetchData();
    } catch (err) {
      console.error('Error deleting member:', err);
      setError('Failed to delete team member');
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const getWorkloadColor = (workload) => {
    if (workload >= 80) return 'error';
    if (workload >= 60) return 'warning';
    return 'success';
  };

  const getRoleColor = (role) => {
    const colors = {
      'Senior Partner': 'error',
      'Partner': 'error',
      'Associate': 'primary',
      'Paralegal': 'secondary',
      'Attorney': 'primary'
    };
    return colors[role] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Team Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Collaborate and manage workload across your team
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Add Team Member
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Team Members */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ mb: 3, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Team Members ({teamMembers.length})
              </Typography>
              {teamMembers.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    No team members yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Add team members to start collaborating
                  </Typography>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                    Add First Team Member
                  </Button>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {teamMembers.map((member) => (
                    <Grid item xs={12} md={6} key={member.id}>
                      <Paper
                        sx={{
                          p: 2,
                          transition: 'all 0.2s',
                          cursor: 'pointer',
                          '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }
                        }}
                        onClick={() => navigate(`/team/${member.id}/performance`)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Avatar
                            sx={{
                              width: 56,
                              height: 56,
                              bgcolor: 'primary.main',
                              fontSize: '1.2rem',
                              fontWeight: 600,
                              mr: 2
                            }}
                          >
                            {getInitials(member.name)}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {member.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                              <Chip label={member.role} size="small" color={getRoleColor(member.role)} />
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {member.specialty || 'General Practice'}
                            </Typography>
                          </Box>
                          <Box>
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDialog(member);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMember(member.id);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                              Active Cases
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {member.active_cases || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">
                              Tasks Completed
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {member.tasks_completed || 0}
                            </Typography>
                          </Grid>
                        </Grid>

                        <Box sx={{ mt: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Workload
                            </Typography>
                            <Typography variant="caption" fontWeight={600}>
                              {member.workload || 0}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={member.workload || 0}
                            color={getWorkloadColor(member.workload || 0)}
                            sx={{ height: 6, borderRadius: 1 }}
                          />
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>

          {/* Workload Overview */}
          {teamMembers.length > 0 && (
            <Card sx={{ boxShadow: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Workload Distribution
                </Typography>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  {teamMembers.map((member) => (
                    <Box key={member.id} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {member.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {member.active_cases || 0} cases • {member.workload || 0}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={member.workload || 0}
                        color={getWorkloadColor(member.workload || 0)}
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Activity Feed */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ boxShadow: 2, height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Team Activity
                </Typography>
                <Badge badgeContent={activityFeed.length} color="primary">
                  <NotificationsIcon />
                </Badge>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {activityFeed.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No recent activity
                  </Typography>
                </Box>
              ) : (
                <List>
                  {activityFeed.map((activity) => (
                    <ListItem
                      key={activity.id}
                      sx={{
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        mb: 1,
                        py: 1.5
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.light' }}>
                          {activity.action && activity.action.includes('completed') ? <CheckCircleIcon /> : <WorkIcon />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            <strong>{activity.user_name || 'User'}</strong> {activity.action || 'performed action'}
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {activity.details || activity.description || ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              <AccessTimeIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                              {getTimeAgo(activity.timestamp || activity.created_at)}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add/Edit Member Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingMember ? 'Edit Team Member' : 'Add Team Member'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            margin="normal"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <TextField
            fullWidth
            select
            label="Role"
            margin="normal"
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
          >
            <MenuItem value="Senior Partner">Senior Partner</MenuItem>
            <MenuItem value="Partner">Partner</MenuItem>
            <MenuItem value="Associate">Associate</MenuItem>
            <MenuItem value="Attorney">Attorney</MenuItem>
            <MenuItem value="Paralegal">Paralegal</MenuItem>
            <MenuItem value="Legal Assistant">Legal Assistant</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Specialty"
            margin="normal"
            value={formData.specialty}
            onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
            placeholder="e.g., Corporate Law, Litigation"
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            margin="normal"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveMember}
            disabled={!formData.name.trim() || !formData.email.trim()}
          >
            {editingMember ? 'Update' : 'Add'} Member
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Team;